// usePantry — list + CRUD for the user's pantry. Single read RPC
// (pantry.list) returns every field the view needs; no per-item
// detail RPC. SSE subscription patches the local list in place when
// another device for the same user mutates, so cross-device sync
// (laptop + Android wrapper) stays live.

import { useCallback, useEffect, useState } from "preact/hooks";
import { api, isOfflineError, peekCache, type PantryItem, type PantryCategory } from "./rpc";
import { sse } from "./sse";

export interface UsePantry {
  items: PantryItem[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  add: (input: AddInput) => Promise<PantryItem | null>;
  update: (id: number, patch: Partial<PantryItem>) => Promise<PantryItem | null>;
  remove: (id: number) => Promise<void>;
  toggleLowStock: (id: number) => Promise<void>;
}

interface AddInput {
  name: string;
  qty?: string;
  category?: PantryCategory;
  location?: string | null;
  expires?: string | null;
  lowStock?: boolean;
  source?: PantryItem["source"];
}

export function usePantry(): UsePantry {
  const [items, setItems] = useState<PantryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);

    // SWR: paint cached pantry items immediately on mount.
    let hadCache = false;
    try {
      const cached = await peekCache<{ ok: true; items: PantryItem[] }>("pantry.list");
      if (cached?.items?.length) {
        hadCache = true;
        setItems(cached.items);
        setLoading(false);
      }
    } catch { /* swallow */ }
    if (!hadCache) setLoading(true);

    try { setItems(await api.pantryList()); }
    catch (e: any) {
      if (!hadCache || !isOfflineError(e)) {
        setError(e?.message || "failed to load pantry");
      }
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Cross-device sync. Other tabs / the Android wrapper emit
  // pantry:created|updated|deleted via SSE; we patch local state so
  // the change shows up without a manual reload.
  useEffect(() => {
    return sse.on((ev: any) => {
      if (ev?.type === "pantry:created" && ev.item) {
        setItems((prev) => {
          // Idempotent — if the item is already there (we created it
          // locally), the server echo shouldn't double it.
          if (prev.some((i) => i.id === ev.item.id)) return prev;
          return [...prev, ev.item];
        });
      } else if (ev?.type === "pantry:updated" && ev.item) {
        setItems((prev) => prev.map((i) => i.id === ev.item.id ? { ...i, ...ev.item } : i));
      } else if (ev?.type === "pantry:deleted" && ev.itemId != null) {
        setItems((prev) => prev.filter((i) => i.id !== ev.itemId));
      }
    });
  }, []);

  const add = useCallback(async (input: AddInput) => {
    const name = (input.name || "").trim();
    if (!name) return null;
    try {
      const item = await api.pantryAdd({ ...input, name });
      setItems((prev) => prev.some((i) => i.id === item.id) ? prev : [...prev, item]);
      return item;
    } catch (e: any) { setError(e?.message || "add failed"); return null; }
  }, []);

  const update = useCallback(async (id: number, patch: Partial<PantryItem>) => {
    // Optimistic — apply the patch locally so the qty/expiry chip
    // updates instantly, then reconcile with the server response.
    setItems((prev) => prev.map((i) => i.id === id ? { ...i, ...patch } : i));
    try {
      const item = await api.pantryUpdate(id, patch);
      setItems((prev) => prev.map((i) => i.id === id ? item : i));
      return item;
    } catch (e: any) { setError(e?.message || "update failed"); return null; }
  }, []);

  const remove = useCallback(async (id: number) => {
    const snapshot = items;
    setItems((prev) => prev.filter((i) => i.id !== id));
    try { await api.pantryDelete(id); }
    catch (e: any) {
      // Re-add on failure so the user doesn't silently lose a row.
      setItems(snapshot);
      setError(e?.message || "delete failed");
    }
  }, [items]);

  const toggleLowStock = useCallback(async (id: number) => {
    const cur = items.find((i) => i.id === id);
    if (!cur) return;
    await update(id, { lowStock: !cur.lowStock });
  }, [items, update]);

  return { items, loading, error, refresh, add, update, remove, toggleLowStock };
}
